import { AccountInfo, AuthenticationResult, EventMessage, EventType } from '@azure/msal-browser';
import { AppConstants } from './core/appConstant';
import { AppInsightsService } from './core/services/app-insights.service';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonFunctions } from './shared/utilities/common-functions';
import { DomSanitizer } from '@angular/platform-browser';
import { FeatureStateService } from './shared/services/featureStateService';
import { filter, Subject, Subscription, take, takeUntil } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { IamWrapperService } from './core/services/iam-wrapper.service';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { ShellService } from './core/services/shell.service';
import { StorageConstants } from './shared/constants/storage-constants';
import { ToastrService } from 'ngx-toastr';
import { UserIdleService } from 'angular-user-idle';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'CDMS-Portal-U2-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit, OnDestroy {

  AppConstants = AppConstants;

  userName = '';
  sessionExpires = false;
  url: any;
  pingSubscription: Subscription;
  userHasNoAccess = false;

  private readonly _destroying$ = new Subject<void>();

  constructor(
    private readonly appInsightsService: AppInsightsService,
    private readonly commonFunctions: CommonFunctions,
    private readonly featureStateService: FeatureStateService,
    private readonly http: HttpClient,
    private readonly iamWrapperService: IamWrapperService,
    private readonly msalBroadcastService: MsalBroadcastService,
    private readonly msalService: MsalService,
    private readonly shellService: ShellService,
    private readonly router: Router,
    private readonly sanitizer: DomSanitizer,
    private readonly toastr: ToastrService,
    private readonly userIdle: UserIdleService
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.shellService.navigationStartUrl = event.url;
      }
    });
    const redirectUrl = localStorage.getItem(StorageConstants.RedirectUri);
    if (redirectUrl === '' || !redirectUrl) {
      localStorage.setItem(StorageConstants.RedirectUri, window.location.href);
    }
    this.commonFunctions.showLoading();
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this._destroying$)
      )
      .subscribe({
        next: (result: EventMessage) => {
          const payload = result.payload as AuthenticationResult;
          this.msalService.instance.setActiveAccount(payload.account);
          this.appInsightsService.userInfo.user = JSON.stringify(payload.account.idTokenClaims?.oid);
          this.userName = payload.account.name;
          localStorage.setItem(StorageConstants.UserName, this.userName);
          const account: AccountInfo = this.msalService.instance.getAllAccounts()[0];
          localStorage.setItem(StorageConstants.AccountInfo, JSON.stringify(account));
        },
        error: error => {
          this.commonFunctions.handleErrorResponse(error, this.toastr);
        }
      });

      this.configureIdealTimeOut();
  }

  ngOnInit(): void {
    this.shellService.logoutObservable$
    .pipe(
      takeUntil(this._destroying$)
    )
    .subscribe({
      next: (isLogOutEventCalled) => {
        if (isLogOutEventCalled) {
          this.close();
        }
      }
    });

    this.shellService.noAccess$
    .pipe(
      takeUntil(this._destroying$)
    )
    .subscribe({
      next: (userHasNoAccess) => {
        if (userHasNoAccess) {
          this.userHasNoAccess = userHasNoAccess;
        }
      }
    });

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        const emptyRoute = (event.url === '/');
        this.disposeSessionStorage(event);
        if (emptyRoute) {
          this.appInsightsService.logPageView(document.title, this.router.url, { url: document.URL });
        }
      }
    });
  }

  onFrameLoad() {
    if (this.url && this.url !== null) {
      sessionStorage.clear();
      localStorage.clear();
      this.commonFunctions.hideLoading();
      window.location.href = window.location.origin;
    }
  }

  onAuthorizationChangeConfirmationDataItem(event) {
    AppConstants.showConformation = false;
    if (event) {
      AppConstants.formDirty = false;
      this.router.navigateByUrl(AppConstants.currentRouteUrl);
    }
  }

  close() {
    this.sessionExpires = false;
    if (this.featureStateService.useIamApis()) {
      this.clearCache();
    }
    else {
      this.logout();
    }
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

  private configureIdealTimeOut(): void {
    this.userIdle.startWatching();
    this.userIdle.onTimerStart().subscribe();
    this.userIdle.onTimeout().subscribe(() => {
      this.sessionExpires = true;
      this.restart();
    });

    this.pingSubscription = this.userIdle.ping$
      .subscribe(() => {
        this.updateUserSession();
      });
  }

  private updateUserSession() {
    return this.http.get<any>(AppConstants.updateUserSession, {
      headers: new HttpHeaders({ 'Content-Type': 'application/json' })
    });
  }

  private restart() {
    this.userIdle.resetTimer();
  }

  private clearCache() {
    this.commonFunctions.showLoading();
    this.iamWrapperService.clearUserCache()
      .pipe(
        take(1),
        takeUntil(this._destroying$)
      ).subscribe({
        next: () => {
          this.logout();
        },
        error: _ => {
          this.logout();
        }
      });
  }

  private logout() {
    this.commonFunctions.showLoading();
    this.url = this.sanitizer.bypassSecurityTrustResourceUrl(AppConstants.LogoutMSALADURL + window.location.href);
    this.msalService.logoutRedirect({
      authority: environment.authority + environment.tenant,
      account: this.msalService.instance.getAllAccounts()[0],
      postLogoutRedirectUri: '/',
      onRedirectNavigate: () =>
        // Return false to stop navigation after local logout
        false
    });
  }

  private disposeSessionStorage(event) {
    if (event.url) {
      const url = event.url.toLowerCase();
      this.disposeSessionStorageStep1(url);
      this.disposeSessionStorageStep2(url);
      this.disposeSessionStorageStep3(url);
      this.disposeSessionStorageStep4(url);
    }
  }

  private disposeSessionStorageStep1(url: any) {
    if (url.indexOf('customeremployeerelation') === -1) {
      sessionStorage.removeItem(StorageConstants.ClientSelectedRelation);
      sessionStorage.removeItem(StorageConstants.EmployeeSelectedRelation);
    }
    if (url.indexOf('payrate') === -1) {
      sessionStorage.removeItem(StorageConstants.ClientSelected);
      sessionStorage.removeItem(StorageConstants.EmployeeSelected);
    }
    if (url.indexOf('shiftmaintenance') === -1) {
      sessionStorage.removeItem(StorageConstants.ShiftMaintenanceSearch);
    }
    if (url.indexOf('billrates') === -1) {
      sessionStorage.removeItem(StorageConstants.PayerSelected);
    }
    if (url.indexOf('caregiverpayrates') === -1) {
      sessionStorage.removeItem(StorageConstants.ClientSelectedRates);
      sessionStorage.removeItem(StorageConstants.CaregiverEmployerSelected);
      sessionStorage.removeItem(StorageConstants.IsCgvEmpRelationshipComboBoxEnabled);
    }
    if (url.indexOf('servicecodes/edit') !== -1
      || url.indexOf('servicecodes/editrate') !== -1
      || url.indexOf('servicecodes/addrate') !== -1) {
      return;
    }
    else {
      sessionStorage.removeItem(StorageConstants.ServiceCodeValues);
      sessionStorage.removeItem(StorageConstants.ServiceCodeValuesDeleted);
    }
  }

  private disposeSessionStorageStep2(url: any) {
    if (url.indexOf('servicecodes') === -1) {
      sessionStorage.removeItem(StorageConstants.ServiceCodeSearch);
    }
    if (url.indexOf('participantinsurance') === -1) {
      sessionStorage.removeItem(StorageConstants.CISearch);
    }
    if (url.indexOf('externalcasemanagers') === -1) {
      sessionStorage.removeItem(StorageConstants.EcmSearch);
    }
    if (url.indexOf('payor') === -1) {
      sessionStorage.removeItem(StorageConstants.PayorSearch);
    }
    if (url.indexOf('program') === -1) {
      sessionStorage.removeItem(StorageConstants.ProgramSearch);
    }
    if (url.indexOf('writeoffcause') === -1) {
      sessionStorage.removeItem(StorageConstants.WriteOffCauseSearch);
    }
  }

  private disposeSessionStorageStep3(url: any) {
    if (url.indexOf('feetype') === -1) {
      sessionStorage.removeItem(StorageConstants.feeTypeSearch);
    }
    if (url.indexOf('fundingtype') === -1) {
      sessionStorage.removeItem(StorageConstants.fundingTypeSearch);
    }
    if (url.indexOf('claimsummary') === -1) {
      sessionStorage.removeItem(StorageConstants.ClaimSummaryInquirySearch);
    }
    if (url.indexOf('feerate') === -1) {
      sessionStorage.removeItem(StorageConstants.FeeRateSearch);
    }
    if (url.indexOf('claimbatchreview') === -1) {
      sessionStorage.removeItem(StorageConstants.ClaimBatchSearch);
    }
    if (url.indexOf('payroll/review') === -1) {
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewRunId);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewSystem);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewStart);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewEnd);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewCaregiver);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewPayor);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewIsAbsence);
      sessionStorage.removeItem(StorageConstants.PayrollBatchReviewIsTimeWorked);
    }
  }

  private disposeSessionStorageStep4(url: any) {
    if (url.indexOf('programtypes') === -1) {
      sessionStorage.removeItem(StorageConstants.ProgramTypeSearch);
    }
    if (url.indexOf('fundingsource') === -1) {
      sessionStorage.removeItem(StorageConstants.FundingSourceSearch);
    }
    if (url.indexOf('diagcode') === -1) {
      sessionStorage.removeItem(StorageConstants.DiagCodeSearch);
    }
    if (url.indexOf('participantrepresentativerelations') === -1) {
      sessionStorage.removeItem(StorageConstants.ClientRepSelectedRelation);
      sessionStorage.removeItem(StorageConstants.RepresentativeSelectedRelation);
    }
    if (url.indexOf('authorizations') === -1) {
      sessionStorage.removeItem(StorageConstants.AuthorizationsSearch);
    }
  }
}
